import React, {useEffect, useState} from 'react';
import st from './Players.module.scss';
import {format} from 'date-fns';
import Table from '../../components/Table/Table';
import ModalForm from '../../components/ModalForm/ModalForm';
import {
    Input,
    Button,
    Popconfirm,
    notification,
    DatePicker,
    Checkbox,
    Dropdown,
    Tabs,
    Select
} from "antd";
import api from '../../config/axiosConfig';
import {EditOutlined, DeleteOutlined, SettingOutlined, ExportOutlined, DollarOutlined} from '@ant-design/icons';
import {useLocation} from "react-router-dom";
import {FilterDropdown, NumberRangeFilter, FilterSearch, exportToExcel} from '../../utils/utils';

const Players = () => {
    const location = useLocation();
    const [statuses, setStatuses] = useState([]);
    const actionBasedStatuses = statuses?.filter(status => status.isActionBased);
    const nonActionBasedStatuses = statuses?.filter(status => !status.isActionBased);
    const [teams, setTeams] = useState([]);
    const [coaches, setCoaches] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [playerIsUpdated, setPlayerIsUpdated] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [currentPlayer, setCurrentPlayer] = useState(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [tableFilters, setTableFilters] = useState({});
    const [tableData, setTableData] = useState([]);
    const [balanceEditMode, setBalanceEditMode] = useState(false);
    const [balancePlayer, setBalancePlayer] = useState(null);

    const handleDataLoaded = (data) => {
        setTableData(data);
    };

    const handleExport = () => {
        if (!tableData || tableData.length === 0) {
            notification.error({
                message: 'Error',
                description: 'No data available to export.',
                placement: 'topRight',
            });
            return;
        }
        exportToExcel({
            data: tableData,
            columns: filteredColumns,
            fileName: 'Players_Data',
            coaches,
            teams,
            actionBasedStatuses,
            nonActionBasedStatuses,
        });
        notification.success({
            message: 'Success',
            description: 'Coaches data exported successfully.',
            placement: 'topRight',
        });
    };

    const coachesList = async () => {
        try {
            const response = await api.get('coaches');
            setCoaches(response.data);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch coaches list.',
                placement: 'topRight',
            });
        }
    }

    const url = location.pathname === '/all-players'
        ? 'students'
        : `students?active=true&page=1`;

    const allColumns = [
        {
            title: 'ID',
            dataIndex: 'identifier',
            sorter: (a, b) => (a.identifier || '').localeCompare(b.identifier || ''),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.identifier.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['identifier'] || null, // Bind filter state
        },
        {
            title: 'მოსწავლე',
            dataIndex: 'studentFullName',
            sorter: (a, b) => (a.studentFullName || '').localeCompare(b.studentFullName || ''),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Student"
                />
            ),
            onFilter: (value, record) => {
                return (record?.studentFullName.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['studentFullName'] || null, // Bind filter state
        },
        {
            title: 'გუნდი',
            dataIndex: 'teamId',
            sorter: (a, b) => (a.teamId || 0) - (b.teamId || 0),
            render: (teamId) => {
                const team = teams?.find(s => s.id === teamId);
                return team?.name;
            },
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterDropdown
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Team"
                    data={teams}
                />
            ),
            onFilter: (value, record) => {
                return record?.teamId === value;
            },
            filteredValue: tableFilters['teamId'] || null, // Bind filter state
        },
        {
            title: 'მწვრთნელი',
            dataIndex: 'coachId',
            sorter: (a, b) => (a.coachId || 0) - (b.coachId || 0),
            render: (coachId) => {
                const coach = coaches?.find(s => s.id === coachId);
                return coach && `${coach?.firstName} ${coach?.lastName}`
            },
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                                 visible,
                             }) => {
                return (
                    <FilterDropdown
                        selectedKeys={selectedKeys}
                        setSelectedKeys={setSelectedKeys}
                        confirm={confirm}
                        clearFilters={clearFilters}
                        placeholder="Search Coach"
                        data={coaches} // Render data only when filter is open
                    />
                );
            },
            onFilter: (value, record) => {
                return record?.coachId === value;
            },
            filteredValue: tableFilters['coachId'] || null,
        },
        {
            title: 'სტატუსი',
            dataIndex: 'statusId',
            sorter: (a, b) => (a.statusId || 0) - (b.statusId || 0),
            render: (statusId) => {
                const status = statuses?.find(s => s.id === statusId);
                return status?.name;
            },
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                                 visible,
                             }) => (
                <FilterDropdown
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                    data={nonActionBasedStatuses}
                />
            ),
            onFilter: (value, record) => {
                return record?.statusId === value;
            },
            filteredValue: tableFilters['statusId'] || null,
        },
        {
            title: 'ქმედების სტატუსი',
            dataIndex: 'actionBasedStatusId',
            sorter: (a, b) => (a.actionBasedStatusId || 0) - (b.actionBasedStatusId || 0),
            render: (actionBasedStatusId) => {
                const status = statuses?.find(s => s.id === actionBasedStatusId);
                return status?.name;
            },
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterDropdown
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Action Status"
                    data={actionBasedStatuses}
                />
            ),
            onFilter: (value, record) => {
                return record?.actionBasedStatusId === value;
            },
            filteredValue: tableFilters['actionBasedStatusId'] || null,
        },
        {
            title: 'ბალანსი',
            dataIndex: 'balance',
            sorter: (a, b) => (a.balance || 0) - (b.balance || 0),
            render: (balance) => {
                let color = 'inherit';
                if (balance > 0) {
                    color = 'green';
                } else if (balance < 0) {
                    color = 'red';
                }
                return <span style={{color}}>{balance}</span>;
            },
            filterDropdown: props => <NumberRangeFilter {...props} />,
            onFilter: (value, record) => {
                const {min, max} = JSON.parse(value);
                let minValue = min !== undefined ? min : -Infinity;
                let maxValue = max !== undefined ? max : Infinity;
                if (min !== undefined && max === undefined) {
                    maxValue = minValue;
                } else if (max !== undefined && min === undefined) {
                    minValue = maxValue;
                }
                if (minValue > maxValue) {
                    [minValue, maxValue] = [maxValue, minValue];
                }
                return record.balance >= minValue && record.balance <= maxValue;
            },
            filteredValue: tableFilters['balance'] || null,
        },
        {
            title: 'კომენტარი',
            dataIndex: 'note',
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            sorter: (a, b) => (a.note || '').localeCompare(b.note || ''),
            onFilter: (value, record) => {
                return (record.note || '').toLowerCase().includes(value.toLowerCase());
            },
            filteredValue: tableFilters['note'] || null,
        },
        {
            title: 'დედა',
            dataIndex: 'mother',
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.mother?.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['mother'] || null,
        },
        {
            title: 'დედის მობ. ნომერი',
            dataIndex: 'motherMobileNumber',
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.motherMobileNumber.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['motherMobileNumber'] || null,
        },
        {
            title: 'მამა',
            dataIndex: 'father',
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.father?.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['father'] || null,
        },
        {
            title: 'მამის მობ. ნომერი',
            dataIndex: 'fatherMobileNumber',
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.fatherMobileNumber.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['fatherMobileNumber'] || null,
        },
        {
            title: 'დაბ. თარიღი',
            dataIndex: 'birthDate',
            sorter: (a, b) => new Date(a.birthDate || 0) - new Date(b.birthDate || 0),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Birthdate"
                />
            ),
            onFilter: (value, record) => {
                return (record?.birthDate.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['birthDate'] || null,
        },
        {
            title: 'მისამართი',
            dataIndex: 'address',
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.address.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['address'] || null,
        },
        {
            title: 'სკოლა',
            dataIndex: 'school',
            sorter: (a, b) => (a.school || '').localeCompare(b.school || ''),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.school.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['school'] || null,
        },
        {
            title: 'ცვლა',
            dataIndex: 'shift',
            sorter: (a, b) => (a.shift || '').localeCompare(b.shift || ''),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.shift.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['shift'] || null,
        },
        {
            title: 'პ/ნ',
            dataIndex: 'personalNumber',
            sorter: (a, b) => (a.personalNumber || '').localeCompare(b.personalNumber || ''),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Personal Number"
                />
            ),
            onFilter: (value, record) => {
                return (record?.personalNumber.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['personalNumber'] || null,
        },
        {
            title: 'დედის პ/ნ',
            dataIndex: 'motherPersonalNumber',
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.motherPersonalNumber.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['motherPersonalNumber'] || null,
        },
        {
            title: 'მამის პ/ნ',
            dataIndex: 'fatherPersonalNumber',
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.fatherPersonalNumber.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['fatherPersonalNumber'] || null,
        },
        {
            title: 'გამოცდილება',
            dataIndex: 'experience',
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.experience.toLowerCase() || '').includes(value.toLowerCase());
            },
            filteredValue: tableFilters['experience'] || null,
        },
        {
            title: 'რეგისტრაციის თარიღი',
            dataIndex: 'registrationDate',
            sorter: (a, b) => new Date(a.registrationDate || 0) - new Date(b.registrationDate || 0),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return record.registrationDate.toLowerCase().includes(value.toLowerCase());
            },
            filteredValue: tableFilters['registrationDate'] || null,
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            render: (text, record) => (
                <>
                    <Button type="link" onClick={() => openEditModal(record)}>
                        <EditOutlined/>
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this player?"
                        onConfirm={() => handleDeletePlayer(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger>
                            <DeleteOutlined/>
                        </Button>
                    </Popconfirm>
                    <Button type="link" onClick={() => openEditBalanceModal(record)}>
                        <DollarOutlined/>
                    </Button>
                </>
            ),
        },
    ];

    const formItems = [
        // {label: 'იდენტიფიკატორი', name: 'identifier', component: <Input/>},
        [
            {label: 'სახელი', name: 'firstName', rules: [{required: true}], component: <Input/>, span: 8},
            {label: 'გვარი', name: 'lastName', rules: [{required: true}], component: <Input/>, span: 8},
            {label: 'პ/ნ', name: 'personalNumber', component: <Input/>, span: 8},
        ],
        [
            {label: 'დაბ. თარიღი', name: 'birthDate', rules: [{required: true}], component: <DatePicker/>, span: 12},
            {label: 'მისამართი', name: 'address', component: <Input/>, span: 12},
        ],
        [
            {label: 'სკოლა', name: 'school', component: <Input/>, span: 12},
            {label: 'ცვლა', name: 'shift', component: <Input/>, span: 12},
        ],
        [
            {label: 'გამოცდილება', name: 'experience', component: <Input/>, span: 24},
        ],
        [
            {label: 'დედა', name: 'mother', component: <Input/>, span: 8},
            {label: 'დედის პ/ნ', name: 'motherPersonalNumber', component: <Input/>, span: 8},
            {label: 'დედის მობ. ნომერი', name: 'motherMobileNumber', component: <Input/>, span: 8},
        ],
        [
            {label: 'მამა', name: 'father', component: <Input/>, span: 8},
            {label: 'მამის პ/ნ', name: 'fatherPersonalNumber', component: <Input/>, span: 8},
            {label: 'მამის მობ. ნომერი', name: 'fatherMobileNumber', component: <Input/>, span: 8},
        ],
        [
            {
                label: 'სტატუსი', name: 'statusId', component: (
                    <Select
                        showSearch={true}
                        placeholder="Select a status"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={nonActionBasedStatuses?.map(status => ({
                            value: status.id,
                            label: status.name
                        }))}
                        allowClear={true}
                    />
                ), span: editMode ? 8 : 12
            },
            {
                label: 'ჯგუფი', name: 'teamId',
                component: (
                    <Select
                        showSearch={true}
                        placeholder="Select a team"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={teams?.map(team => ({
                            value: team.id,
                            label: team.name
                        }))}
                        allowClear={true}
                    />
                ),
                span: editMode ? 8 : 12
            },
            ...(editMode ? [{
                label: 'ქმედების სტატუსი', name: 'actionBasedStatusId',
                component: (
                    <Select
                        showSearch={true}
                        placeholder="Select a status"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={actionBasedStatuses?.map(status => ({
                            value: status.id,
                            label: status.name,
                        }))}
                        allowClear={true}
                    />)
                , span: 8
            }] : []),
        ],
        [
            {label: 'კომენტარი', name: 'note', component: <Input.TextArea/>, span: 24},
        ],
        // {label: 'ბალანსი', name: 'balance', component: <Input type="number"/>},
        //         // {label: 'რეგისტრაციის თარიღი', name: 'registrationDate', component: <DatePicker/>},
    ];

    const balanceEditFormItems = [
        [
            {label: 'ბალანსი', name: 'balance', component: <Input type="number"/>, span: 24},
            {label: 'კომენტარი', name: 'note', component: <Input.TextArea/>, span: 24},
        ],
    ];

    const columnKeys = allColumns.map((col) => col.key || col.dataIndex);
    const getInitialCheckedList = () => {
        const savedColumns = localStorage.getItem('checkedColumns');
        return savedColumns ? JSON.parse(savedColumns) : columnKeys;
    };
    const [checkedList, setCheckedList] = useState(getInitialCheckedList());
    const filteredColumns = allColumns?.filter(
        (col) => checkedList.includes(col.key || col.dataIndex)
    );
    const handleCheckboxChange = (checkedValue) => {
        if (checkedValue.includes('all')) {
            const newCheckedList = checkedList.length === columnKeys.length ? [] : columnKeys;
            setCheckedList(newCheckedList);
            localStorage.setItem('checkedColumns', JSON.stringify(newCheckedList));
        } else {
            setCheckedList(checkedValue);
            localStorage.setItem('checkedColumns', JSON.stringify(checkedValue));
        }
    };

    const dropdownContent = (
        <div style={{padding: '10px', width: '200px', background: '#ffffff'}}>
            <Checkbox.Group
                style={{display: 'flex', flexDirection: 'column'}}
                value={checkedList}
                onChange={handleCheckboxChange}
            >
                <Checkbox indeterminate={checkedList.length === columnKeys.length} value="all"
                          checked={checkedList.length === columnKeys.length}>
                    All
                </Checkbox>
                {allColumns.map(({title, key, dataIndex}) => (
                    <Checkbox key={key || dataIndex} value={key || dataIndex}>
                        {title}
                    </Checkbox>
                ))}
            </Checkbox.Group>
            <Button
                type="primary"
                style={{marginTop: '10px', width: '100%'}}
                onClick={() => setDropdownOpen(false)}
            >
                Close
            </Button>
        </div>
    );

    const handleAddEditPlayer = async (values) => {
        setLoading(true);
        try {
            const formattedValues = {
                ...values,
                birthDate: values.birthDate ? values.birthDate.format('YYYY-MM-DD') : null,
                registrationDate: values.registrationDate ? values.registrationDate.format('YYYY-MM-DD HH:mm:ss') : null
            };
            if (currentPlayer) {
                // Edit Player
                await api.put(`students/${currentPlayer.id}`, formattedValues);
                notification.success({
                    message: 'Success',
                    description: 'Player updated successfully',
                    placement: 'topRight'
                });
            } else {
                // Add Player
                await api.post('students/create', formattedValues);
                notification.success({
                    message: 'Success',
                    description: 'Player added successfully',
                    placement: 'topRight'
                });
            }
            setIsModalOpen(false);
            setPlayerIsUpdated(!playerIsUpdated);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to save player. Please try again.',
                placement: 'topRight'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEditBalance = async (values) => {
        setLoading(true);
        const balanceValues = {
            studentIdentifier: balancePlayer?.identifier || '', // Extract identifier from balancePlayer
            balanceAfter: Number(values.balance) ?? null, // Ensure balanceAfter is a number
            note: values.note || '', // Include the note field
        }
        try {
            await api.post('balance-log/create', balanceValues);
            notification.success({
                message: 'Success',
                description: 'Player added successfully',
                placement: 'topRight'
            });
            setIsModalOpen(false);
            setPlayerIsUpdated(!playerIsUpdated);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to save player balance. Please try again.',
                placement: 'topRight'
            });
        } finally {
            setLoading(false);
        }
    }

    const openEditModal = (player) => {
        setCurrentPlayer(player);
        setEditMode(true);
        setIsModalOpen(true);
        setBalanceEditMode(false);
    };

    const openEditBalanceModal = (balance) => {
        setBalancePlayer(balance); // Set the current player
        setBalanceEditMode(true); // Open the modal
        setIsModalOpen(true);
    };

    const handleDeletePlayer = async (id) => {
        setLoading(true);
        try {
            await api.delete(`students/${id}`);
            notification.success({
                message: 'Success',
                description: 'Player has been successfully deleted.',
                placement: 'topRight',
            });
            setPlayerIsUpdated(prev => !prev);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to delete the player. Please try again.',
                placement: 'topRight',
            });
        } finally {
            setLoading(false);
        }
    };

    const statusesList = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('statuses', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setStatuses(response.data);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch coaches list.',
                placement: 'topRight',
            });
        }
    };
    const teamsList = async () => {
        try {
            const response = await api.get('teams');
            setTeams(response.data);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch teams list.',
                placement: 'topRight',
            });
        }
    }


    useEffect(() => {
        statusesList();
        teamsList();
    }, [isModalOpen]);

    useEffect(() => {
        coachesList()
    }, []);

    useEffect(() => {
        // Save the current state in localStorage whenever it changes
        localStorage.setItem('checkedColumns', JSON.stringify(checkedList));
    }, [checkedList]);

    const nestedColumns = [
        {title: 'ტრანზაქციის ID', dataIndex: 'transactionId', width: 100,},
        {title: 'თარიღი', dataIndex: 'issueDate', width: 100,},
        {title: 'თანხა', dataIndex: 'amount', width: 100,},
        {title: 'დანიშნულება', dataIndex: 'additionalData', width: 100,},
        {
            title: 'გადახდის მეთოდი',
            dataIndex: 'isTBCPay',
            width: 150,
            sorter: (a, b) => (a.isTBCPay === b.isTBCPay ? 0 : a.isTBCPay ? -1 : 1),
            render: (isTBCPay) => {
                if (isTBCPay) {
                    return (
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
                            <img
                                src="/TBC.png"
                                alt="Payment Method"
                                style={{width: '20px'}}
                            />
                        </div>
                    );
                } else {
                    return (
                        <div>
                            ანგარიშის ნომრით
                        </div>
                    );
                }
            }
        }
    ];
    const formatDate = (isoDate) => {
        return format(new Date(isoDate), 'yyyy-MM-dd HH:mm:ss'); // Desired format
    };
    const nestedColumns_2 = [
        {title: 'იდენთიფიკატორი', dataIndex: 'studentIdentifier', width: 100,},
        {title: 'ჩამოსაჭრელი თანხა', dataIndex: 'serviceFee', width: 100,},
        {title: 'ბალანსი ჩამოჭრამდე', dataIndex: 'balanceBefore', width: 100,},
        {title: 'ბალანსი ჩამოჭრის შემდეგ', dataIndex: 'balanceAfter', width: 100,},
        {
            title: 'თარიღი', dataIndex: 'createdAt', width: 100,
            render: (date) => formatDate(date)
        },
    ];

    const nestedColumns_3 = [
        {title: 'ბალანსი კორექციამდე', dataIndex: 'balanceBefore', width: 100,},
        {title: 'ბალანსი კორექციის შემდეგ', dataIndex: 'balanceAfter', width: 100,},
        {title: 'კომენტარი', dataIndex: 'note', width: 100,},
        {
            title: 'თარიღი', dataIndex: 'changeTimestamp', width: 100,
            render: (date) => formatDate(date)
        },
    ];

    const nestedColumns_4 = [
        {title: 'ცვლილების ტიპი', dataIndex: 'changeType', width: 100,},
        {
            title: 'ძველი მწვრთნელი', dataIndex: 'oldCoachId', width: 100, render: (oldCoachId) => {
                const coach = coaches?.find(s => s.id === oldCoachId);
                return coach && `${coach?.firstName} ${coach?.lastName}`
            },
        },
        {
            title: 'ახალი მწვრთნელი', dataIndex: 'newCoachId', width: 100, render: (newCoachId) => {
                const coach = coaches?.find(s => s.id === newCoachId);
                return coach && `${coach?.firstName} ${coach?.lastName}`
            }
        },
        {
            title: 'ძველი გუნდი', dataIndex: 'oldTeamId', width: 100, render: (oldTeamId) => {
                const team = teams?.find(s => s.id === oldTeamId);
                return team && team?.name;
            },
        },
        {
            title: 'ახალი გუნდი', dataIndex: 'newTeamId', width: 100, render: (newTeamId) => {
                const team = teams?.find(s => s.id === newTeamId);
                return team && team?.name;
            }
        },
        {
            title: 'თარიღი', dataIndex: 'changeTimestamp', width: 100,
            render: (date) => formatDate(date)
        },
    ];

    // Expanded row render function
    const expandedRowRender = (record) => (
        <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="გადახდები" key="1">
                <Table
                    columns={nestedColumns}
                    url={`payments/get-students-payments/${record.identifier}`}
                    pagination={false}
                    size="small"
                />
            </Tabs.TabPane>
            <Tabs.TabPane tab="დარიცხვები" key="2">
                <Table
                    columns={nestedColumns_2}
                    url={`accruals/get-students-accruals/${record.identifier}`}
                    pagination={true}
                    size="small"
                />
            </Tabs.TabPane>
            <Tabs.TabPane tab="კორექციები" key="3">
                <Table
                    columns={nestedColumns_3}
                    url={`balance-log/${record.identifier}`}
                    pagination={true}
                    size="small"
                />
            </Tabs.TabPane>
            <Tabs.TabPane tab="მწვრთნელები" key="4">
                <Table
                    columns={nestedColumns_4}
                    url={`change-logs/${record.identifier}`}
                    pagination={true}
                    size="small"
                />
            </Tabs.TabPane>
        </Tabs>
    );

    const handleRowDoubleClick = (record) => {
        const isExpanded = expandedRowKeys.includes(record.id);
        setExpandedRowKeys(isExpanded ? expandedRowKeys?.filter((key) => key !== record.id) : [...expandedRowKeys, record.id]);
    };

    return (
        <>
            <div className={st.btnContainer}>
                <Button
                    type="primary"
                    onClick={() => {
                        setIsModalOpen(true);
                        setEditMode(false);
                        setBalanceEditMode(false);
                        setCurrentPlayer(null);
                    }}
                    style={{marginBottom: 16}}
                >
                    Add Player
                </Button>
                <Dropdown
                    overlay={dropdownContent}
                    trigger={['click']}
                    open={dropdownOpen}
                    onOpenChange={(open) => setDropdownOpen(open)}
                >
                    <Button onClick={() => setDropdownOpen(!dropdownOpen)}>
                        <SettingOutlined/> Columns
                    </Button>
                </Dropdown>
                <Button
                    type="primary"
                    style={{marginBottom: 16}}
                    onClick={() => setTableFilters({})} // Reset filters state
                >
                    Clear All Filters
                </Button>
                <Button
                    type="default"
                    icon={<ExportOutlined/>}
                    onClick={handleExport}
                    style={{marginBottom: 16, marginLeft: 8}}
                >
                    Export to Excel
                </Button>
            </div>
            <ModalForm
                open={isModalOpen}
                title={balanceEditMode ? 'Edit Player Balance' : editMode ? "Edit Player" : "Add New Player"}
                onCancel={() => setIsModalOpen(false)}
                onSubmit={balanceEditMode ? handleEditBalance : handleAddEditPlayer}
                confirmLoading={loading}
                formItems={balanceEditMode ? balanceEditFormItems : formItems}
                initialValues={balanceEditMode
                    ? {balance: balancePlayer?.balance}
                    : currentPlayer}
                className='playersModal'
                rowIsUpdated={playerIsUpdated}
            />
            <Table
                columns={filteredColumns}
                url={url}
                rowIsUpdated={playerIsUpdated}
                rowKey={(record) => record.id}
                expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpandedRowsChange: setExpandedRowKeys,
                }}
                onRow={(record) => ({
                    onDoubleClick: () => handleRowDoubleClick(record), // Expand on double-click
                    style: {
                        backgroundColor: record.balance > 0
                            ? 'rgba(0, 255, 0, 0.1)' // Light green for positive balance
                            : record.balance < 0
                                ? 'rgba(255, 0, 0, 0.1)' // Light red for negative balance
                                : 'inherit', // Default for balance = 0
                    },
                })}
                searchPlaceholder="Search Players"
                tableFilters={tableFilters}
                setTableFilters={setTableFilters}
                onDataLoaded={handleDataLoaded}
            />
        </>
    );
}

export default Players;